import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Separator } from '@marine/ui/separator';
import { Slider } from '@marine/ui/slider';
import { Switch } from '@marine/ui/switch';
import { Textarea } from '@marine/ui/textarea';
import { AiModelSelect } from '@marine/ui/AiModelSelect';
import { typedResolver } from '@/lib/typedResolver';
import {
  usePlatformAIModels,
  useRefreshPlatformAIModels,
} from '@/modules/platform/hooks/usePlatformAIModels';
import {
  useTenantAiConfig,
  useUpdateTenantAiConfig,
  type UpdateTenantAiConfigInput,
} from '@/modules/platform/services/tenantAiAdminApi';

const safeNumber = (schema: z.ZodNumber) =>
  z.preprocess((v) => {
    if (v === '' || v === null || v === undefined) return undefined;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isNaN(n) ? undefined : n;
  }, schema.optional());

const safeNullableNumber = (schema: z.ZodNumber) =>
  z.preprocess((v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isNaN(n) ? null : n;
  }, schema.nullable().optional());

const aiConfigSchema = z.object({
  openrouter_model: z.string().optional(),
  perplexity_model: z.string().optional(),
  ai_temperature: safeNumber(z.number().min(0).max(2)),
  ai_max_tokens: safeNumber(z.number().min(1).max(100000)),
  vision_model: z.string().optional().nullable(),
  specs_model: z.string().optional().nullable(),
  description_model: z.string().optional().nullable(),
  pricing_model: z.string().optional().nullable(),
  daily_ai_limit: safeNullableNumber(z.number().min(0)),
  monthly_ai_limit: safeNullableNumber(z.number().min(0)),
  banned_words: z.string().optional().nullable(),
  include_location_in_descriptions: z.boolean().optional(),
  auto_analyze_photos: z.boolean().optional(),
});

type AIConfigFormData = z.infer<typeof aiConfigSchema>;

interface TenantAiConfigSectionProps {
  tenantId: string;
}

/**
 * Platform → tenant detail → AI Config tab (#1029). Per-tenant equivalent of
 * the deleted dealer-facing AiConfigTab.tsx — same fields, same
 * progressive-disclosure shape (enable toggle always visible, advanced form
 * gated behind it) — but every save writes into THIS tenant's own
 * `dealership_settings` row, and `auto_analyze_photos` actually persists
 * here (F4 defect 1: it never did through the dealer-facing form, because
 * that FormRequest was missing the validation rule for it).
 */
export function TenantAiConfigSection({ tenantId }: TenantAiConfigSectionProps) {
  const { data: settings, isLoading, isError } = useTenantAiConfig(tenantId);
  const updateMutation = useUpdateTenantAiConfig(tenantId);
  const { data: modelsData, isLoading: isModelsLoading } = usePlatformAIModels();
  const refreshModelsMutation = useRefreshPlatformAIModels();

  const aiForm = useForm<AIConfigFormData>({
    resolver: typedResolver(aiConfigSchema),
    values: {
      openrouter_model: settings?.openrouter_model || 'openai/gpt-4',
      perplexity_model: settings?.perplexity_model || 'sonar',
      ai_temperature: settings?.ai_temperature ?? 0.7,
      ai_max_tokens: settings?.ai_max_tokens ?? 2000,
      vision_model: settings?.vision_model || null,
      specs_model: settings?.specs_model || null,
      description_model: settings?.description_model || null,
      pricing_model: settings?.pricing_model || null,
      daily_ai_limit: settings?.daily_ai_limit ?? null,
      monthly_ai_limit: settings?.monthly_ai_limit ?? null,
      banned_words: settings?.banned_words ?? '',
      include_location_in_descriptions: settings?.include_location_in_descriptions ?? false,
      auto_analyze_photos: settings?.auto_analyze_photos ?? false,
    },
  });

  const aiEnabled = settings?.ai_enabled ?? false;
  const isSaving = updateMutation.isPending;

  const handleToggleAI = useCallback(
    (checked: boolean) => {
      updateMutation.mutate(
        { ai_enabled: checked },
        {
          onSuccess: () => toast.success(checked ? 'AI enabled for this dealer' : 'AI disabled for this dealer'),
          onError: () => toast.error('Failed to update AI enable state'),
        },
      );
    },
    [updateMutation],
  );

  const handleModelChange = useCallback(
    (field: 'vision_model' | 'specs_model' | 'description_model' | 'pricing_model', value: string) => {
      aiForm.setValue(field, value === '__none__' ? null : value);
    },
    [aiForm],
  );

  const handleSubmit = useCallback(
    (data: AIConfigFormData) => {
      const payload: UpdateTenantAiConfigInput = {
        openrouter_model: data.openrouter_model,
        perplexity_model: data.perplexity_model,
        ai_temperature: data.ai_temperature,
        ai_max_tokens: data.ai_max_tokens,
        vision_model: data.vision_model === '__none__' ? null : (data.vision_model ?? null),
        specs_model: data.specs_model === '__none__' ? null : (data.specs_model ?? null),
        description_model: data.description_model === '__none__' ? null : (data.description_model ?? null),
        pricing_model: data.pricing_model === '__none__' ? null : (data.pricing_model ?? null),
        daily_ai_limit: data.daily_ai_limit,
        monthly_ai_limit: data.monthly_ai_limit,
        banned_words: data.banned_words || null,
        include_location_in_descriptions: data.include_location_in_descriptions ?? false,
        auto_analyze_photos: data.auto_analyze_photos ?? false,
      };

      updateMutation.mutate(payload, {
        onSuccess: () => toast.success('AI configuration saved'),
        onError: () => toast.error('Failed to save AI configuration'),
      });
    },
    [updateMutation],
  );

  const models = modelsData ?? [];

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12">
        <p className="text-sm text-destructive">Failed to load AI configuration for this tenant</p>
        <p className="text-xs text-muted-foreground">Please try refreshing the page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading AI configuration…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + Enable toggle */}
      <div className="flex items-center justify-between">
        <h3 className="ms-1 flex items-center gap-1.5 text-sm font-semibold">
          <Sparkles className="size-3.5 opacity-60" />
          AI Configuration
        </h3>
        <Badge variant={aiEnabled ? 'success' : 'secondary'} size="sm">
          {aiEnabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      {/* Enable toggle — always visible, saves immediately on change */}
      <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
        <div>
          <Label htmlFor="ai_enabled">Enable AI integration</Label>
          <p className="text-2xs text-muted-foreground">
            Enable AI-powered features like description generation, image analysis, and spec lookup
            for this dealer.
          </p>
        </div>
        <Switch
          id="ai_enabled"
          checked={aiEnabled}
          onCheckedChange={handleToggleAI}
          disabled={isSaving}
        />
      </div>

      {/* Model params, overrides, content controls — only when enabled */}
      {aiEnabled && (
        <form
          onSubmit={aiForm.handleSubmit(handleSubmit, (errors) => {
            console.error('AI Config form validation errors:', errors);
            toast.error('Please fix validation errors before saving');
          })}
          className="space-y-6"
        >
          {/* Provider API keys are platform-owned; manage them on the AI
              Keys tab on this same page — not a dealer-facing concept here. */}
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
            Provider API keys (OpenRouter, Perplexity, Anthropic) for this dealer are managed on
            the <span className="font-medium text-foreground">AI Keys</span> tab. Model preferences,
            generation parameters, and content controls for this dealer are set below.
          </div>

          {/* Default OpenRouter / Perplexity model pickers */}
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-3 rounded-lg border border-border p-4">
              <p className="text-sm font-medium">OpenRouter</p>
              <AiModelSelect
                label="Default Model"
                value={aiForm.watch('openrouter_model') || null}
                onChange={(v) => aiForm.setValue('openrouter_model', v === '__none__' ? '' : v)}
                models={Array.isArray(models) ? models : undefined}
                isLoading={isModelsLoading}
              />
            </div>
            <div className="space-y-3 rounded-lg border border-border p-4">
              <p className="text-sm font-medium">Perplexity</p>
              <div className="space-y-2">
                <Label htmlFor="perplexity_model">Default Model</Label>
                <Input
                  id="perplexity_model"
                  placeholder="sonar"
                  {...aiForm.register('perplexity_model')}
                />
                <p className="text-2xs text-muted-foreground">
                  Perplexity uses its own model IDs (e.g., sonar, sonar-pro).
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Section B: Model Parameters */}
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold">Model Parameters</p>
              <p className="text-2sm text-muted-foreground">
                Fine-tune AI behavior for content generation for this dealer.
              </p>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="ai_temperature">Temperature</Label>
                  <span className="text-sm text-muted-foreground">
                    {Number.isFinite(Number(aiForm.watch('ai_temperature')))
                      ? Number(aiForm.watch('ai_temperature')).toFixed(2)
                      : '0.70'}
                  </span>
                </div>
                <Slider
                  id="ai_temperature"
                  min={0}
                  max={2}
                  step={0.1}
                  value={[aiForm.watch('ai_temperature') || 0.7]}
                  onValueChange={(value) => aiForm.setValue('ai_temperature', value[0])}
                />
                <p className="text-2xs text-muted-foreground">
                  Lower values make output more focused, higher values more creative.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai_max_tokens">Max Tokens</Label>
                <Input
                  id="ai_max_tokens"
                  type="number"
                  min={1}
                  max={100000}
                  {...aiForm.register('ai_max_tokens', { valueAsNumber: true })}
                />
                <p className="text-2xs text-muted-foreground">
                  Maximum length of generated content (1-100,000).
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Section C: Per-Function Model Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">AI Model Overrides</p>
                <p className="text-2sm text-muted-foreground">
                  Select specific models for each AI function. Leave as &quot;Use default&quot;
                  to use the global model above.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => refreshModelsMutation.mutate()}
                disabled={refreshModelsMutation.isPending}
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${refreshModelsMutation.isPending ? 'animate-spin' : ''}`}
                />
                Refresh Models
              </Button>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <AiModelSelect
                label="Image Analysis"
                value={aiForm.watch('vision_model') || null}
                onChange={(v) => handleModelChange('vision_model', v)}
                filterVision={true}
                models={Array.isArray(models) ? models : undefined}
                isLoading={isModelsLoading}
                onRefresh={() => refreshModelsMutation.mutate()}
                isRefreshing={refreshModelsMutation.isPending}
              />
              <AiModelSelect
                label="Spec Lookup"
                value={aiForm.watch('specs_model') || null}
                onChange={(v) => handleModelChange('specs_model', v)}
                filterVision={false}
                models={Array.isArray(models) ? models : undefined}
                isLoading={isModelsLoading}
                onRefresh={() => refreshModelsMutation.mutate()}
                isRefreshing={refreshModelsMutation.isPending}
              />
              <AiModelSelect
                label="Description"
                value={aiForm.watch('description_model') || null}
                onChange={(v) => handleModelChange('description_model', v)}
                filterVision={false}
                models={Array.isArray(models) ? models : undefined}
                isLoading={isModelsLoading}
                onRefresh={() => refreshModelsMutation.mutate()}
                isRefreshing={refreshModelsMutation.isPending}
              />
              <AiModelSelect
                label="Pricing"
                value={aiForm.watch('pricing_model') || null}
                onChange={(v) => handleModelChange('pricing_model', v)}
                filterVision={false}
                models={Array.isArray(models) ? models : undefined}
                isLoading={isModelsLoading}
                onRefresh={() => refreshModelsMutation.mutate()}
                isRefreshing={refreshModelsMutation.isPending}
              />
            </div>
          </div>

          <Separator />

          {/* Section D: Cost Controls */}
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold">Cost Controls</p>
              <p className="text-2sm text-muted-foreground">
                Set spending limits for this dealer&apos;s AI operations.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="daily_ai_limit">Daily AI Limit ($)</Label>
                <Input
                  id="daily_ai_limit"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="No limit"
                  {...aiForm.register('daily_ai_limit', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="monthly_ai_limit">Monthly AI Limit ($)</Label>
                <Input
                  id="monthly_ai_limit"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="No limit"
                  {...aiForm.register('monthly_ai_limit', { valueAsNumber: true })}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Automation section */}
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold">Automation</p>
              <p className="text-2sm text-muted-foreground">
                Configure automatic AI actions that fire without a manual click.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div>
                <Label htmlFor="auto_analyze_photos">Auto-analyze photos on upload</Label>
                <p className="text-2xs text-muted-foreground">
                  When enabled, uploading photos in the Add-Boat wizard dispatches image analysis
                  automatically for this dealer. Analyzes the first 3 photos after uploads settle.
                </p>
              </div>
              <Switch
                id="auto_analyze_photos"
                checked={aiForm.watch('auto_analyze_photos') ?? false}
                onCheckedChange={(checked) =>
                  aiForm.setValue('auto_analyze_photos', checked, {
                    shouldDirty: true,
                  })
                }
              />
            </div>
          </div>

          <Separator />

          {/* Section E: Content Controls */}
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold">Content Controls</p>
              <p className="text-2sm text-muted-foreground">
                Configure how AI generates content for this dealer.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div>
                <Label htmlFor="include_location_in_descriptions">
                  Include location in descriptions
                </Label>
                <p className="text-2xs text-muted-foreground">
                  Automatically include this dealer&apos;s city and state in AI-generated
                  descriptions.
                </p>
              </div>
              <Switch
                id="include_location_in_descriptions"
                checked={aiForm.watch('include_location_in_descriptions') ?? false}
                onCheckedChange={(checked) =>
                  aiForm.setValue('include_location_in_descriptions', checked, {
                    shouldDirty: true,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="banned_words">Banned Words</Label>
              <Textarea
                id="banned_words"
                rows={4}
                placeholder="Enter words or phrases to exclude from AI-generated content, one per line..."
                className="font-mono text-xs"
                {...aiForm.register('banned_words')}
              />
              <p className="text-2xs text-muted-foreground">
                One word or phrase per line. AI will be instructed to avoid these in all
                generated content for this dealer (descriptions, specs, quotes, etc.).
              </p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => aiForm.reset()}>
              Reset
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
